'use client';
import classNames from 'classnames';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { ReactNode, useEffect, useMemo, useState } from 'react';

import { useArticlesData, useArticleTagsData } from '@/entities/article';
import { Bullet, Filter, QueryStatus } from '@/shared/ui';
import { ArticleFullList, ArticleFilteredList } from '@/widgets/article';
import TagFilters from '@/widgets/filters/ui/tagFilters/TagFilters';

import styles from './blogPage.module.scss';

const BlogPage = ({
  initialTags,
  breadcrumbs,
}: {
  initialTags: string[];
  breadcrumbs?: ReactNode;
}) => {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const [selectedTags, setSelectedTags] = useState<string[]>(initialTags);
  const [pendingTags, setPendingTags] = useState<string[]>(initialTags);

  useEffect(() => {
    const tagsList = searchParams
      .getAll('tags')
      .flatMap((tag) => tag.split(','))
      .filter(Boolean);
    setSelectedTags(tagsList);
    setPendingTags(tagsList);
  }, [searchParams]);

  const { data, isError, isLoading, refetch } = useArticlesData(selectedTags);
  const {
    data: tags,
    isError: tagsError,
    refetch: refetchTags,
  } = useArticleTagsData();

  const tagOptions = useMemo(() => {
    return (
      tags?.map((tag) => ({
        label: tag.name,
        value: tag.id,
      })) || []
    );
  }, [tags]);

  const onFilterChange = (tagId: number) => {
    const stringId = String(tagId);
    setPendingTags((prev) =>
      prev.includes(stringId)
        ? prev.filter((id) => id !== stringId)
        : [...prev, stringId],
    );
  };

  const onFilterAccept = () => {
    onChooseTag(pendingTags);
  };

  const onFilterClear = () => {
    setPendingTags([]);
    handleFilterClear();
  };

  function onChooseTag(newTags: string[]) {
    setSelectedTags(newTags);
    setPendingTags(newTags);

    const params = new URLSearchParams(searchParams.toString());
    if (newTags.length > 0) {
      params.set('tags', newTags.join(','));
    } else {
      params.delete('tags');
    }

    router.push(`${pathname}?${params.toString()}`, { scroll: false });
  }

  function handleFilterClear() {
    setSelectedTags([]);
    const params = new URLSearchParams(searchParams.toString());
    params.delete('tags');
    router.push(`${pathname}?${params.toString()}`, { scroll: false });
  }

  const filteredArticles = useMemo(() => {
    if (!data) return [];
    return data.flatMap((cat) =>
      cat.subcategories.flatMap((sub) => sub.articles),
    );
  }, [data]);

  const filterTitle = useMemo(() => {
    if (!tags || selectedTags.length === 0) return '';

    return tags
      .filter((tag) => selectedTags.includes(String(tag.id)))
      .map((tag) => tag.name)
      .join(', #');
  }, [tags, selectedTags]);

  return (
    <main className={classNames('page', styles.blogPage)}>
      <div className={classNames('container')}>
        <div className={styles.blogPage__head}>
          <h1 className={styles.blogPage__title}>Статьи</h1>
          <div className={styles.blogPage__textBlock}>
            <p className={styles.blogPage__text}>
              Методичка содержит в себе ряд статей для самостоятельного изучения
              темы чая
            </p>
            <Filter
              className={classNames('is-mob', styles.blogPage__filter)}
              label="Теги"
              options={tagOptions}
              value={pendingTags.map(Number)}
              variant={'bordered'}
              onAccept={onFilterAccept}
              onChange={onFilterChange}
              onClear={onFilterClear}
            >
              {selectedTags.length > 0 && (
                <Bullet count={selectedTags.length} variant="border-black-24" />
              )}
            </Filter>
          </div>
        </div>
        <QueryStatus
          isError={isError || tagsError}
          isLoading={isLoading}
          onRetry={() => {
            refetch();
            refetchTags();
          }}
        />
        <div className={styles.blogPage__content}>
          <div className={classNames('is-desk', styles.blogPage__list)}>
            {tags && tags?.length > 0 && (
              <TagFilters
                selectedTags={selectedTags}
                tags={tags}
                onChooseTag={onChooseTag}
              />
            )}
          </div>
          {!isError &&
            (selectedTags.length > 0 ? (
              <ArticleFilteredList
                className={styles.blogPage__articles}
                count={filteredArticles.length}
                title={filterTitle}
                value={filteredArticles}
                onClear={handleFilterClear}
              />
            ) : (
              <ArticleFullList
                className={styles.blogPage__articles}
                value={data}
              />
            ))}
        </div>
        {breadcrumbs}
      </div>
    </main>
  );
};

export default BlogPage;
